import { useMemo, useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  Grid,
  CircularProgress,
  Button,
} from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import SearchIcon from '@material-ui/icons/Search';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import { useNavigate, useLocation } from 'react-router-dom';
import { MarketplaceProvider, useMarketplace } from './MarketplaceContext';
import { FavoritesProvider } from './FavoritesContext';
import { MarketplaceCard } from './MarketplaceCard';
import { ItemType } from './types';

const useStyles = makeStyles((theme: Theme) => ({
  root: { minHeight: '100vh', background: theme.palette.background.default },
  header: {
    background: `linear-gradient(135deg, #00101F 0%, #001B3D 35%, #003075 65%, #006699 100%)`,
    padding: theme.spacing(4, 4, 3),
  },
  headerPrompt: {
    background: `linear-gradient(135deg, #00101F 0%, #001B3D 35%, #003075 65%, #004F80 100%)`,
    padding: theme.spacing(4, 4, 3),
  },
  backBtn: {
    color: 'rgba(255,255,255,0.8)',
    marginBottom: theme.spacing(1),
    textTransform: 'none',
    fontSize: '0.85rem',
    padding: 0,
    '&:hover': { color: '#fff', background: 'none' },
  },
  pageTitle: { fontWeight: 800, fontSize: '2rem', color: '#fff' },
  pageDesc: {
    color: 'rgba(255,255,255,0.8)',
    marginTop: theme.spacing(0.5),
    fontSize: '0.95rem',
  },
  searchField: {
    background: 'rgba(255,255,255,0.12)',
    borderRadius: theme.spacing(4),
    maxWidth: 400,
    marginTop: theme.spacing(2),
    '& .MuiOutlinedInput-root': {
      borderRadius: theme.spacing(4),
      color: '#fff',
      '& fieldset': { border: '1.5px solid rgba(255,255,255,0.3)' },
      '&:hover fieldset': { border: '1.5px solid rgba(255,255,255,0.6)' },
      '&.Mui-focused fieldset': { border: '2px solid #fff' },
    },
    '& .MuiInputAdornment-root .MuiSvgIcon-root': {
      color: 'rgba(255,255,255,0.6)',
    },
    '& input': { color: '#fff' },
  },
  body: { maxWidth: 1200, margin: '0 auto', padding: theme.spacing(3) },
  countLabel: {
    color: theme.palette.text.secondary,
    fontSize: '0.85rem',
    marginBottom: theme.spacing(2),
  },
}));

interface ListPageProps {
  type: Extract<ItemType, 'skill' | 'prompt'>;
}

function ListPageInner({ type }: ListPageProps) {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { skills, prompts, loading, error } = useMarketplace();

  const items = type === 'skill' ? skills : prompts;
  const meta =
    type === 'skill'
      ? {
          title: 'Skills',
          desc: 'Extend GitHub Copilot with reusable skill definitions',
        }
      : {
          title: 'Instructions',
          desc: 'Custom prompt instructions for GitHub Copilot',
        };

  const initialQuery = new URLSearchParams(location.search).get('q') ?? '';
  const [search, setSearch] = useState(initialQuery);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    if (!q) return items;
    return items.filter(
      i =>
        i.displayName.toLowerCase().includes(q) ||
        i.description.toLowerCase().includes(q) ||
        i.keywords.some(k => k.toLowerCase().includes(q)),
    );
  }, [items, search]);

  const headerClass = type === 'skill' ? classes.header : classes.headerPrompt;

  return (
    <Box className={classes.root}>
      <Box className={headerClass}>
        <Button
          startIcon={<ArrowBackIcon />}
          className={classes.backBtn}
          disableRipple
          onClick={() => navigate('/ai-marketplace')}
        >
          AI Marketplace
        </Button>
        <Typography className={classes.pageTitle}>{meta.title}</Typography>
        <Typography className={classes.pageDesc}>{meta.desc}</Typography>
        <TextField
          variant="outlined"
          size="small"
          placeholder={`Search ${type}s…`}
          value={search}
          onChange={e => setSearch(e.target.value)}
          className={classes.searchField}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <Box className={classes.body}>
        {loading && (
          <Box textAlign="center" py={6}>
            <CircularProgress />
          </Box>
        )}
        {!loading && !!error && (
          <Typography color="error">Failed to load: {error}</Typography>
        )}
        {!loading && !error && (
          <>
            <Typography className={classes.countLabel}>
              {filtered.length} of {items.length} {type}s
            </Typography>
            <Grid container spacing={2}>
              {filtered.map(item => (
                <Grid item xs={12} sm={6} md={4} lg={3} key={item.id}>
                  <MarketplaceCard item={item} />
                </Grid>
              ))}
            </Grid>
            {filtered.length === 0 && (
              <Typography
                style={{ textAlign: 'center', opacity: 0.5, paddingTop: 40 }}
              >
                No {type}s match your search.
              </Typography>
            )}
          </>
        )}
      </Box>
    </Box>
  );
}

export function SkillsPage() {
  return (
    <MarketplaceProvider>
      <FavoritesProvider>
        <ListPageInner type="skill" />
      </FavoritesProvider>
    </MarketplaceProvider>
  );
}

export function PromptsPage() {
  return (
    <MarketplaceProvider>
      <FavoritesProvider>
        <ListPageInner type="prompt" />
      </FavoritesProvider>
    </MarketplaceProvider>
  );
}
